import React from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';

import { MambuIcon } from '~/img';
import VeenguIcon from '~/components/VeenguIcon';
import { TopLevelEntry } from '~/types/layoutMenu';

type BuildMenuStructureParams = {
  handleKycAppClick: () => void;
  isMambuActive: boolean;
  mambuIconClass: string;
};

// To add a new item: insert a RouteItem in the relevant group's children array.
// To add a new group: insert a GroupItem with its paths[] and children[].
// To add a divider: insert { type: 'divider' }.
export const buildMenuStructure = ({
  handleKycAppClick,
  isMambuActive,
  mambuIconClass,
}: BuildMenuStructureParams): TopLevelEntry[] => [
  {
    type: 'route',
    path: '/',
    label: 'home',
    icon: <VeenguIcon name='house' />,
    collapsesAll: true,
  },
  {
    type: 'group',
    labelKey: 'reportsSubmenu',
    shortLabelKey: 'reportsSubmenuShort',
    icon: <VeenguIcon name='chart-pie' />,
    paths: ['/reports/virtualLiquidityPosition', '/reports/endUsersActivity', '/reports/'],
    children: [
      {
        type: 'route',
        path: '/reports/endUsersActivity',
        label: 'endUsersActivity',
        icon: <VeenguIcon name='trending-up' />,
        permission: 'report.endUsersActivity.view',
        isNew: true,
      },
      {
        type: 'route',
        path: '/reports/virtualLiquidityPosition',
        label: 'virtualLiquidityPosition',
        icon: <VeenguIcon name='gauge' />,
        permission: 'virtualLiquidityPosition.view',
      },
      {
        type: 'route',
        path: '/reports/partners',
        label: 'partnersReport',
        icon: <VeenguIcon name='handshake' />,
        requireAllPermissions: ['report.partners.view'],
        isNew: true,
      },
      {
        type: 'route',
        path: '/reports/tenantAccounts',
        label: 'tenantAccountsReport',
        icon: <VeenguIcon name='chart-area' />,
        requireAllPermissions: ['report.tenantAccounts.view'],
        isNew: true,
      },
    ],
  },
  {
    type: 'group',
    labelKey: 'transactionsSubmenu',
    shortLabelKey: 'transactionsSubmenu',
    icon: <VeenguIcon name='arrow-left-right' />,
    paths: ['/headTransactions', '/deals', '/transaction/transfers', '/batches'],
    children: [
      {
        type: 'route',
        path: '/headTransactions',
        label: 'headTransactions',
        icon: <VeenguIcon name='receipt-text' />,
        permission: 'headTransaction.list',
      },
      {
        type: 'route',
        path: '/deals',
        label: 'deals',
        icon: <VeenguIcon name='globe' />,
        permission: 'deal.list',
      },
      {
        type: 'route',
        path: '/transaction/transfers',
        label: 'transfers',
        icon: <VeenguIcon name='rows-3' />,
        permission: 'transaction.transfers.list',
      },
      {
        type: 'route',
        path: '/batches',
        label: 'batches',
        icon: <VeenguIcon name='layers' />,
        permission: 'batchPayment.list',
      },
    ],
  },
  { type: 'divider' },
  {
    type: 'route',
    path: '/individuals',
    label: 'individuals',
    icon: <VeenguIcon name='user' />,
    permission: 'individual.list',
    collapsesAll: true,
  },
  {
    type: 'group',
    labelKey: 'businesses',
    icon: <VeenguIcon name='building-2' />,
    paths: ['/businesses', '/employees'],
    children: [
      {
        type: 'route',
        path: '/businesses',
        label: 'businesses',
        icon: <VeenguIcon name='store' />,
        permission: 'business.list',
      },
      {
        type: 'route',
        path: '/employees',
        label: 'businessTeamMembers',
        icon: <VeenguIcon name='users' />,
        permission: 'business.employee.list',
      },
    ],
  },
  {
    type: 'route',
    path: '/accounts',
    label: 'accounts',
    icon: <VeenguIcon name='wallet' />,
    permission: 'account.list',
    collapsesAll: true,
  },
  { type: 'divider' },
  {
    type: 'group',
    labelKey: 'workflowsAndApprovals',
    shortLabelKey: 'workflowsAndApprovalsShort',
    icon: <VeenguIcon name='workflow' />,
    paths: ['/approvals'],
    children: [
      {
        type: 'route',
        path: '/approvals/queue',
        label: 'pendingApprovalCases',
        icon: <VeenguIcon name='gavel' />,
        permission: [
          'approvalCase.list.pending.eligibleForDecision',
          'approvalCase.list.pending.all',
          'approvalCase.admin',
        ],
        licenseKey: 'veengu_tenant_approval_cases',
        isNew: true,
      },
      {
        type: 'route',
        path: '/approvals/history',
        label: 'completedApprovalCases',
        icon: <VeenguIcon name='check-check' />,
        permission: ['approvalCase.list.history.all', 'approvalCase.admin'],
        licenseKey: 'veengu_tenant_approval_cases',
        isNew: true,
      },
      {
        type: 'action',
        label: 'tasklistApp',
        icon: (loading: boolean) =>
          loading ? <CircularProgress size={24} /> : <VeenguIcon name='list-checks' />,
        permission: 'kyc.app',
        onClick: handleKycAppClick,
      },
    ],
  },
  { type: 'divider' },
  {
    type: 'group',
    labelKey: 'financialParameters',
    shortLabelKey: 'financialParametersShort',
    icon: <VeenguIcon name='circle-dollar' />,
    paths: ['/pricings', '=/fxrates'],
    children: [
      {
        type: 'route',
        path: '/pricings',
        label: 'pricing',
        icon: <VeenguIcon name='tags' />,
        permission: 'solution.pricing.list',
      },
      {
        type: 'route',
        path: '/fxrates',
        label: 'fxRates',
        icon: <VeenguIcon name='repeat' />,
        permission: 'fxrates.data.view',
        exact: true,
      },
    ],
  },
  {
    type: 'group',
    labelKey: 'configuration',
    shortLabelKey: 'configurationShort',
    icon: <VeenguIcon name='settings-2' />,
    paths: [
      '/solutions',
      '/paymentServices',
      '/localization',
      '/menu',
      '/applicationRules',
      '/scripts',
      '/profileTags',
      '/fxrates/setup',
      '/tenant/accounts',
      '/bank/accounts',
    ],
    children: [
      {
        type: 'route',
        path: '/solutions',
        label: 'solutions',
        icon: <VeenguIcon name='star' />,
        permission: 'solution.list',
      },
      {
        type: 'route',
        path: '/paymentServices',
        label: 'paymentServices',
        icon: <VeenguIcon name='credit-card' />,
        permission: 'paymentService.list',
      },
      {
        type: 'route',
        path: '/localization',
        label: 'localization',
        icon: <VeenguIcon name='message-square' />,
        permission: 'localization.list',
      },
      {
        type: 'route',
        path: '/profileTags',
        label: 'profileTags',
        icon: <VeenguIcon name='tag' />,
        permission: 'profileTag.list',
      },
      {
        type: 'route',
        path: '/fxrates/setup',
        label: 'currenciesAndFx',
        icon: <VeenguIcon name='coins' />,
        permission: 'fxrates.data.view',
      },
      {
        type: 'route',
        path: '/tenant/accounts',
        label: 'pnlAccounts',
        icon: <VeenguIcon name='landmark' />,
        permission: 'tenant.account.list',
      },
      {
        type: 'route',
        path: '/bank/accounts',
        label: 'liquidityBuckets',
        icon: <VeenguIcon name='droplets' />,
        permission: 'bank.account.list',
      },
      {
        type: 'route',
        path: '/menu',
        label: 'menu',
        icon: <VeenguIcon name='list' />,
        permission: 'dynamicMenu.list',
      },
      {
        type: 'route',
        path: '/applicationRules',
        label: 'appsUpdate',
        icon: <VeenguIcon name='package' />,
        permission: 'externalApplication.list',
        licenseKey: 'veengu_unbound_mobile_apps_force_update',
        isNew: true,
      },
      {
        type: 'route',
        path: '/scripts',
        label: 'scripts',
        icon: <VeenguIcon name='file-code' />,
        permission: 'script.list',
        isNew: true,
      },
    ],
  },
  {
    type: 'group',
    labelKey: 'integrations',
    icon: <VeenguIcon name='plug' />,
    paths: ['/partners', '/externalProfileLinks', '/actionReports'],
    children: [
      {
        type: 'route',
        path: '/partners',
        label: 'partners',
        icon: <VeenguIcon name='briefcase' />,
        permission: 'partner.list',
      },
      {
        type: 'route',
        path: '/externalProfileLinks',
        label: 'externalProfileLinks',
        icon: <VeenguIcon name='link' />,
        permission: 'externalProfileLink.list',
      },
      {
        type: 'route',
        path: '/actionReports',
        label: 'actionReports',
        icon: <VeenguIcon name='chart' />,
        permission: 'report.list',
      },
      {
        type: 'group',
        labelKey: 'ledgers',
        icon: <VeenguIcon name='book-text' />,
        paths: ['/ledgers'],
        children: [
          {
            type: 'conditional',
            when: isMambuActive,
            item: {
              type: 'route',
              path: '/ledgers/mambu',
              label: 'mambu',
              icon: <MambuIcon className={mambuIconClass} />,
            },
          },
        ],
      },
    ],
  },
  {
    type: 'group',
    labelKey: 'credentials',
    shortLabelKey: 'credentialsShort',
    icon: <VeenguIcon name='key-round' />,
    paths: ['/apiIdentities', '/team', '/roles'],
    children: [
      {
        type: 'route',
        path: '/apiIdentities',
        label: 'apiIdentities',
        icon: <VeenguIcon name='id-card' />,
        permission: 'apiIdentity.list',
      },
      {
        type: 'route',
        path: '/team',
        label: 'team',
        icon: <VeenguIcon name='users-round' />,
        permission: 'identity.list',
      },
      {
        type: 'route',
        path: '/roles',
        label: 'roles',
        icon: <VeenguIcon name='shield' />,
        permission: ['dashboardRole.create', 'dashboardRole.update', 'dashboardRole.delete'],
      },
    ],
  },
  {
    type: 'group',
    labelKey: 'monitoring',
    shortLabelKey: 'monitoringShort',
    icon: <VeenguIcon name='activity' />,
    paths: ['/events', '/auditLog', '/partitionArchive'],
    children: [
      {
        type: 'route',
        path: '/events',
        label: 'events',
        icon: <VeenguIcon name='zap' />,
        permission: 'event.list',
      },
      {
        type: 'route',
        path: '/auditLog',
        label: 'auditLog',
        icon: <VeenguIcon name='file-text' />,
        permission: 'auditLog.list',
      },
      {
        type: 'route',
        path: '/partitionArchive',
        label: 'partitionArchive',
        icon: <VeenguIcon name='database' />,
        permission: 'tenant.partitionArchive.view',
      },
    ],
  },
];
